import React, { useState } from 'react';
import {
  Box,
  Button,
  Chip,
  Collapse,
  Container,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { Speed as SpeedIcon, Tune as TuneIcon } from '@mui/icons-material';
import { useGlobal } from '../context/GlobalContext';
import {
  TRANSACTION_GAS_STRATEGY_ESTIMATE,
  TRANSACTION_GAS_STRATEGY_FIXED,
  TRANSACTION_PREBUILD_LEVEL_NONE,
  TRANSACTION_PREBUILD_LEVEL_RAW,
  TRANSACTION_PREHEAT_MODE_DYNAMIC,
  TRANSACTION_PREHEAT_MODE_PREPARED,
} from '../utils/transactionPreheat';

const toggleSx = {
  '& .MuiToggleButton-root': {
    minHeight: { xs: 40, sm: 34 },
    px: { xs: 1.25, sm: 1.1 },
    py: 0.35,
  },
};

const TransactionPreheatControl = () => {
  const {
    rpcStatus,
    transactionPreheatSettings,
    setTransactionPreheatSettings,
  } = useGlobal();
  const [mobileOpen, setMobileOpen] = useState(false);

  const updateSettings = (patch) => {
    setTransactionPreheatSettings((prev) => ({ ...prev, ...patch }));
  };

  const handleModeChange = (_event, value) => {
    if (!value) return;
    updateSettings({ preheatMode: value });
  };

  const handleGasStrategyChange = (_event, value) => {
    if (!value) return;
    updateSettings({ gasStrategy: value });
  };

  const handleGasLimitChange = (event) => {
    updateSettings({ fixedGasLimit: event.target.value.replace(/[^\d]/g, '') });
  };

  const handlePrebuildLevelChange = (_event, value) => {
    if (!value) return;
    updateSettings(value === TRANSACTION_PREBUILD_LEVEL_RAW
      ? {
        prebuildLevel: value,
        preheatMode: TRANSACTION_PREHEAT_MODE_PREPARED,
        gasStrategy: TRANSACTION_GAS_STRATEGY_FIXED,
      }
      : { prebuildLevel: value });
  };

  const prepared = transactionPreheatSettings.preheatMode === TRANSACTION_PREHEAT_MODE_PREPARED;
  const fixedGas = transactionPreheatSettings.gasStrategy === TRANSACTION_GAS_STRATEGY_FIXED;
  const rawPreSign = transactionPreheatSettings.prebuildLevel === TRANSACTION_PREBUILD_LEVEL_RAW;
  const statusLabel = rawPreSign
    ? (rpcStatus.connected ? 'Raw Data 模式已啟用' : '等待 RPC')
    : prepared
      ? (rpcStatus.connected ? 'Nonce/Fee 已預熱' : '等待 RPC')
      : '即時查詢';

  const controls = (
    <Stack
      direction="row"
      spacing={1}
      useFlexGap
      flexWrap="wrap"
      alignItems="center"
    >
      <ToggleButtonGroup
        exclusive
        size="small"
        value={transactionPreheatSettings.preheatMode}
        onChange={handleModeChange}
        aria-label="transaction preheat mode"
        sx={toggleSx}
      >
        <ToggleButton value={TRANSACTION_PREHEAT_MODE_DYNAMIC}>動態組單</ToggleButton>
        <ToggleButton value={TRANSACTION_PREHEAT_MODE_PREPARED}>預備模式</ToggleButton>
      </ToggleButtonGroup>

      <ToggleButtonGroup
        exclusive
        size="small"
        value={transactionPreheatSettings.gasStrategy}
        onChange={handleGasStrategyChange}
        aria-label="transaction gas mode"
        sx={toggleSx}
      >
        <Tooltip title="交易前估算 GasLimit，較穩但送單前會多一次查詢">
          <ToggleButton value={TRANSACTION_GAS_STRATEGY_ESTIMATE}>EstimateGas</ToggleButton>
        </Tooltip>
        <Tooltip title="直接使用固定 GasLimit，適合追求最快送單">
          <ToggleButton value={TRANSACTION_GAS_STRATEGY_FIXED}>固定 GasLimit</ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>

      {fixedGas && (
        <TextField
          size="small"
          label="GasLimit"
          value={transactionPreheatSettings.fixedGasLimit}
          onChange={handleGasLimitChange}
          inputProps={{ inputMode: 'numeric', pattern: '[0-9]*' }}
          sx={{ width: 128, '& .MuiInputBase-input': { py: 0.75 } }}
        />
      )}

      <ToggleButtonGroup
        exclusive
        size="small"
        value={transactionPreheatSettings.prebuildLevel}
        onChange={handlePrebuildLevelChange}
        aria-label="transaction prebuild level"
        sx={toggleSx}
      >
        <Tooltip title="不預先建立交易資料，命中時即時組單">
          <ToggleButton value={TRANSACTION_PREBUILD_LEVEL_NONE}>關閉預建</ToggleButton>
        </Tooltip>
        <Tooltip title="固定策略會盡可能預建並預簽；動態 Launchpad 跟單會預備已知資料，命中後立即完成簽名">
          <ToggleButton value={TRANSACTION_PREBUILD_LEVEL_RAW}>Raw Data</ToggleButton>
        </Tooltip>
      </ToggleButtonGroup>
    </Stack>
  );

  return (
    <Box
      sx={{
        borderBottom: '1px solid rgba(144, 202, 249, 0.10)',
        bgcolor: 'rgba(13, 17, 21, 0.92)',
      }}
    >
      <Container maxWidth="xl" sx={{ py: { xs: 0.75, sm: 1 } }}>
        <Stack
          direction="row"
          spacing={1}
          useFlexGap
          flexWrap="wrap"
          alignItems="center"
        >
          <Stack direction="row" spacing={0.75} alignItems="center">
            <SpeedIcon fontSize="small" color={prepared ? 'success' : 'disabled'} />
            <Typography variant="caption" color="text.secondary">
              交易預熱
            </Typography>
          </Stack>

          <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            {controls}
          </Box>

          <Button
            size="small"
            variant="outlined"
            startIcon={<TuneIcon />}
            onClick={() => setMobileOpen((value) => !value)}
            sx={{ display: { xs: 'inline-flex', sm: 'none' }, minHeight: 40 }}
          >
            預熱設定
          </Button>

          <Chip
            size="small"
            color={rawPreSign && rpcStatus.connected ? 'success' : prepared && rpcStatus.connected ? 'info' : 'default'}
            label={statusLabel}
          />
        </Stack>

        <Collapse in={mobileOpen} unmountOnExit>
          <Box sx={{ display: { xs: 'block', sm: 'none' }, pt: 1 }}>
            {controls}
          </Box>
        </Collapse>
      </Container>
    </Box>
  );
};

export default TransactionPreheatControl;
